import { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { showError, showSuccess } from '../utils/alerts';
import RegionComunaSelects from '../components/common/RegionComunaSelects';
import { REGIMENES_TRIBUTARIOS } from '../services/regimenesTributarios';
import '../styles/components/EmpresaForm.css';
import '../styles/pages/CreateCompanyPage.css';

const CreateCompanyPage = ({ token }) => {
  const [form, setForm] = useState({
    name: '',
    rut: '',
    address: '',
    rep_legal: '',
    rut_representante: '',
    comuna: '',
    region: '',
    email: '',
    phone: '',
    tipo_empresa: '',
    regimen_tributario: '',
    tasa_ppm: '',
    clave_sii: '',
    conciliacion_bancaria: '',
    recursos_humanos: ''
  });
  const [message, setMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  // Respuesta a «¿Eres empresario individual con RUT personal?»: null mientras no responde
  const [esEmpresarioIndividual, setEsEmpresarioIndividual] = useState(null);
  const navigate = useNavigate();

  const validatePhone = (value) => {
    if (!value) return ''; // opcional: vacío es válido
    if (!value.startsWith('56')) return 'El teléfono debe comenzar con 56 (ej: 56912345678)';
    if (value.length !== 11) return 'El teléfono debe tener exactamente 11 dígitos (ej: 56912345678)';
    if (!/^\d+$/.test(value)) return 'El teléfono debe contener solo números';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setPhoneError(validatePhone(value));
    }
    const cambios = { [name]: value };
    // Empresario individual: nombre y RUT de la empresa son los del representante legal
    if (esEmpresarioIndividual && name === 'rep_legal') cambios.name = value;
    if (esEmpresarioIndividual && name === 'rut_representante') cambios.rut = value;
    setForm({ ...form, ...cambios });
  };

  const responderEmpresarioIndividual = (respuesta) => {
    setEsEmpresarioIndividual(respuesta);
    if (respuesta) {
      setForm({ ...form, name: form.rep_legal, rut: form.rut_representante });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validatePhone(form.phone);
    if (error) {
      setPhoneError(error);
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/company/companies`, { ...form, empresario_individual: esEmpresarioIndividual }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Empresa creada exitosamente');
      setMessage('Empresa creada exitosamente');
      navigate('/select-company');
    } catch (error) {
      console.error('Error al crear empresa:', error);

      // Mostrar mensaje específico del servidor
      const errorMessage = error.response?.data?.message || 'Error al crear empresa';

      // Si es error de límite, mostrar mensaje personalizado; el resto (incl. RUT/teléfono duplicado) usa el mensaje del servidor
      if (error.response?.status === 403) {
        showError('Has alcanzado el límite de empresas. Contacta a ventas para contratar un nuevo plan.');
      } else {
        showError(errorMessage);
      }

      setMessage(errorMessage);
    }
  };
  if (esEmpresarioIndividual === null) {
    return (
      <div className="create-company-container">
        <h1>Crear Empresa</h1>
        <div className="create-company-pregunta">
          <h2>¿Eres empresario individual con RUT personal?</h2>
          <p>Persona natural que inició actividades en primera categoría con su propio RUT.</p>
          <div className="create-company-pregunta__botones">
            <button type="button" onClick={() => responderEmpresarioIndividual(true)}>Sí</button>
            <button type="button" onClick={() => responderEmpresarioIndividual(false)}>No</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-company-container">
      <h1>Crear Empresa</h1>
      <p className="create-company-respuesta">
        {esEmpresarioIndividual ? 'Empresario individual con RUT personal.' : 'No es empresario individual con RUT personal.'}{' '}
        <button type="button" className="create-company-respuesta__cambiar" onClick={() => setEsEmpresarioIndividual(null)}>
          Cambiar respuesta
        </button>
      </p>
      <form className="create-company-form" onSubmit={handleSubmit}>
        <div className="empresa-campo">
          <label htmlFor="empresa-name" className="empresa-campo__etiqueta">Nombre de la empresa</label>
          <input
            id="empresa-name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={esEmpresarioIndividual ? 'Se copia del representante legal' : 'Ej: Comercial Los Andes SpA'}
            readOnly={esEmpresarioIndividual}
            required
          />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-rut" className="empresa-campo__etiqueta">RUT de la empresa</label>
          <input
            id="empresa-rut"
            name="rut"
            value={form.rut}
            onChange={handleChange}
            placeholder={esEmpresarioIndividual ? 'Se copia del RUT representante legal' : 'Ej: 76.123.456-7'}
            readOnly={esEmpresarioIndividual}
            required
          />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-address" className="empresa-campo__etiqueta">Dirección</label>
          <input id="empresa-address" name="address" value={form.address} onChange={handleChange} placeholder="Ej: Av. Providencia 1234, of. 56" required />
        </div>
        <RegionComunaSelects
          region={form.region}
          comuna={form.comuna}
          onChange={(cambios) => setForm({ ...form, ...cambios })}
        />
        <div className="empresa-campo">
          <label htmlFor="empresa-rep-legal" className="empresa-campo__etiqueta">Representante legal</label>
          <input id="empresa-rep-legal" name="rep_legal" value={form.rep_legal} onChange={handleChange} placeholder="Nombre y apellido" required />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-rut-representante" className="empresa-campo__etiqueta">RUT representante legal</label>
          <input id="empresa-rut-representante" name="rut_representante" value={form.rut_representante} onChange={handleChange} placeholder="Ej: 12.345.678-9" required />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-email" className="empresa-campo__etiqueta">
            Email <span className="empresa-campo__opcional">opcional</span>
          </label>
          <input id="empresa-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="Ej: contacto@empresa.cl" />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-phone" className="empresa-campo__etiqueta">
            Teléfono <span className="empresa-campo__opcional">opcional</span>
          </label>
          <input
            id="empresa-phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Ej: 56912345678"
            maxLength={11}
          />
          {phoneError && <p className="empresa-campo__error">{phoneError}</p>}
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-clave-sii" className="empresa-campo__etiqueta">Clave SII</label>
          <input
            id="empresa-clave-sii"
            name="clave_sii"
            type="password"
            value={form.clave_sii}
            onChange={handleChange}
            placeholder="Para automatizar el RCV"
          />
        </div>
        {/* Oculto temporalmente: aún faltan pruebas. No eliminar.
            Al quedar sin valor, el backend lo guarda como false. */}
        {/* <select
          name="conciliacion_bancaria"
          value={form.conciliacion_bancaria}
          onChange={(e) => setForm({ ...form, conciliacion_bancaria: e.target.value })}
          required>
          <option value="" disabled>¿Conciliación Bancaria?</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </select> */}
        <div className="empresa-campo">
          <label htmlFor="empresa-recursos-humanos" className="empresa-campo__etiqueta">Recursos humanos</label>
          <select
            id="empresa-recursos-humanos"
            name="recursos_humanos"
            value={form.recursos_humanos}
            onChange={(e) => setForm({ ...form, recursos_humanos: e.target.value })}
            required>
            <option value="" disabled>Selecciona una opción</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-tipo" className="empresa-campo__etiqueta">Tipo de empresa</label>
          <select
            id="empresa-tipo"
            name="tipo_empresa"
            value={form.tipo_empresa}
            onChange={handleChange}
            required>
            <option value="" disabled>Selecciona el tipo de empresa</option>
            <option value="Producto">Producto</option>
            <option value="Servicio">Servicio</option>
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-regimen" className="empresa-campo__etiqueta">Régimen tributario</label>
          <select
            id="empresa-regimen"
            name="regimen_tributario"
            value={form.regimen_tributario}
            onChange={handleChange}
            required>
            <option value="" disabled>Selecciona el régimen tributario</option>
            {REGIMENES_TRIBUTARIOS.map((regimen) => (
              <option key={regimen.codigo} value={regimen.codigo}>{regimen.nombre}</option>
            ))}
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-tasa-ppm" className="empresa-campo__etiqueta">Tasa PPM</label>
          <div className="empresa-campo__con-sufijo">
            <input
              id="empresa-tasa-ppm"
              name="tasa_ppm"
              type="number"
              step="0.001"
              min="0.001"
              value={form.tasa_ppm}
              onChange={handleChange}
              placeholder="Ej: 0.25"
              required
            />
            <span className="empresa-campo__sufijo">%</span>
          </div>
        </div>
        <div className="empresa-acciones">
          <button type="submit">Crear Empresa</button>
        </div>
      </form>
      <p className="create-company-hint">
        Selecciona en tipo de empresa, la opción <strong>Servicios</strong> si tu empresa solo vende servicios de consultoría como ser contadores, abogados, arquitectos, etc., y selecciona <strong>Productos</strong> si tu empresa vende cualquier tipo de producto.
      </p>
      {message && <p>{message}</p>}
    </div>
  );
};

export default CreateCompanyPage;